import { ReactNode, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputLabel,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { UserEntity, GroupEntity, parseEntityRef } from '@backstage/catalog-model';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@backstage/core-components';
import { countryCodeToFlag } from '../../../utils/countryFlag';

/** Compose the dropdown label: "Name — Role, Office", omitting missing parts. */
export function formatPickerLabel(
  name: string,
  role: string | undefined,
  office: string | undefined,
): string {
  const parts = [role, office].filter(Boolean).join(', ');
  return parts ? `${name} — ${parts}` : name;
}

const useStyles = makeStyles(theme => ({
  root: { position: 'relative' },
  dropdown: {
    position: 'absolute',
    zIndex: 1300,
    width: '100%',
    maxHeight: 280,
    overflow: 'auto',
  },
  listItem: {
    cursor: 'pointer',
    '&:hover': { background: theme.palette.action.hover },
  },
  selected: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(0.5),
  },
  membership: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  avatarSelected: {
    width: theme.spacing(3.5),
    height: theme.spacing(3.5),
  },
  avatarResult: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
}));

export interface PersonnelPickerProps {
  value: string | undefined; // user entity ref e.g. "user:default/joel-harley"
  onChange: (ref: string | undefined) => void;
  label: string;
  /** Entity refs already used in other role slots on the same form */
  disabledRefs?: string[];
  /**
   * Fired only when a new person is selected into this slot (not on clear) —
   * mirrors MembersPicker's onMemberAdded (PAT-779), so a parent can react to
   * "someone was just assigned this leadership role" the same way it reacts
   * to a regular member being added.
   */
  onMemberAdded?: (ref: string) => void;
  /** Fired only when the assigned person is cleared — the mirror of onMemberAdded. */
  onMemberRemoved?: (ref: string) => void;
  /**
   * Optional action rendered next to the assigned person (e.g. a
   * primary-team star toggle) — mirrors MembersPicker's renderMemberAction.
   */
  renderMemberAction?: (ref: string) => ReactNode;
}

interface UserRow {
  ref: string;
  name: string;
  email: string;
  picture?: string;
  memberships: string[];
  country?: string;
  role?: string;
  office?: string;
}

export function PersonnelPicker({
  value,
  onChange,
  label,
  disabledRefs = [],
  onMemberAdded,
  onMemberRemoved,
  renderMemberAction,
}: PersonnelPickerProps) {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | undefined>();

  // Load team memberships once for display in results (FR-031: show memberships, not job title)
  const [groupTitles, setGroupTitles] = useState<Map<string, string>>(
    new Map(),
  );
  useEffect(() => {
    catalogApi
      .getEntities({
        filter: { kind: 'Group', 'spec.type': 'team' },
        fields: ['metadata.name', 'metadata.title', 'spec.members'],
      })
      .then(res => {
        const map = new Map<string, string>();
        (res.items as GroupEntity[]).forEach(g => {
          (g.spec?.members as string[] | undefined)?.forEach(memberRef => {
            if (!map.has(memberRef)) {
              map.set(memberRef, g.metadata.title ?? g.metadata.name);
            }
          });
        });
        setGroupTitles(map);
      })
      .catch(() => {});
  }, [catalogApi]);

  // Resolve selected user display when value is pre-populated
  useEffect(() => {
    if (!value) {
      setSelectedUser(undefined);
      return;
    }
    catalogApi
      .getEntityByRef(value)
      .then(entity => {
        if (!entity) return;
        const u = entity as UserEntity;
        setSelectedUser({
          ref: value,
          name: u.spec?.profile?.displayName ?? u.metadata.name,
          email: u.spec?.profile?.email ?? '',
          picture: u.spec?.profile?.picture,
          memberships: [],
          country: u.metadata.annotations?.['user/country'],
          role: u.metadata.annotations?.['user/role'],
          office: u.metadata.annotations?.['user/office'],
        });
      })
      .catch(() => {});
  }, [value, catalogApi]);

  useEffect(() => {
    if (!query.trim() || query.length < 2) {
      setUsers([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    const q = query.toLowerCase();
    catalogApi
      .getEntities({
        filter: { kind: 'User' },
        fields: [
          'metadata.name',
          'metadata.namespace',
          'metadata.annotations',
          'spec.profile.displayName',
          'spec.profile.email',
          'spec.profile.picture',
        ],
      })
      .then(res => {
        const matched = (res.items as UserEntity[])
          .filter(u => {
            const name = (u.spec?.profile?.displayName ?? u.metadata.name).toLowerCase();
            const email = (u.spec?.profile?.email ?? '').toLowerCase();
            const role = (u.metadata.annotations?.['user/role'] ?? '').toLowerCase();
            const office = (u.metadata.annotations?.['user/office'] ?? '').toLowerCase();
            return (
              name.includes(q) ||
              email.includes(q) ||
              role.includes(q) ||
              office.includes(q)
            );
          })
          .slice(0, 10)
          .map(u => {
            // Namespace matters — see MembersPicker's same fix for why.
            const ref = `user:${u.metadata.namespace ?? 'default'}/${u.metadata.name}`;
            return {
              ref,
              name: u.spec?.profile?.displayName ?? u.metadata.name,
              email: u.spec?.profile?.email ?? '',
              picture: u.spec?.profile?.picture,
              memberships: groupTitles.has(ref) ? [groupTitles.get(ref)!] : [],
              country: u.metadata.annotations?.['user/country'],
              role: u.metadata.annotations?.['user/role'],
              office: u.metadata.annotations?.['user/office'],
            };
          });
        setUsers(matched);
        setOpen(matched.length > 0);
      })
      .finally(() => setLoading(false));
  }, [query, catalogApi, groupTitles]);

  const handleSelect = (user: UserRow) => {
    // Replacing an already-occupied slot (e.g. swapping who's Tech Lead) is
    // a removal of the prior occupant, not just an add of the new one — a
    // parent relying on onMemberRemoved (e.g. TeamEditForm's mandatory
    // PAT-780 reassignment prompt) needs to see it as such, or the prior
    // occupant's stale primary team just gets silently reconciled away
    // instead of prompting for a replacement.
    const displacedRef = selectedUser?.ref ?? value;
    if (displacedRef && displacedRef !== user.ref) {
      onMemberRemoved?.(displacedRef);
    }
    onChange(user.ref);
    onMemberAdded?.(user.ref);
    setSelectedUser(user);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    // selectedUser (internal state, set directly by handleSelect) reflects
    // who's actually displayed right now — the value prop itself may not
    // have round-tripped back through a parent's onChange yet.
    const clearedRef = selectedUser?.ref ?? value;
    if (clearedRef) onMemberRemoved?.(clearedRef);
    onChange(undefined);
    setSelectedUser(undefined);
    setQuery('');
  };

  // Namespace matters for the catalog link too — see MembersPicker's same
  // fix for why (e.g. user:development/guest, not user:default/guest).
  const selectedUserRef = selectedUser
    ? parseEntityRef(selectedUser.ref, { defaultKind: 'user', defaultNamespace: 'default' })
    : undefined;

  return (
    <Box className={classes.root}>
      {selectedUser ? (
        <>
          <InputLabel shrink>{label}</InputLabel>
          <Box className={classes.selected}>
            <Avatar src={selectedUser.picture} className={classes.avatarSelected}>
              {selectedUser.name.charAt(0)}
            </Avatar>
          <Box flex={1}>
            <Link to={`/catalog/${selectedUserRef!.namespace}/user/${selectedUserRef!.name}`}>
              <Typography variant="body2">
                {selectedUser.name}
                {selectedUser.country && (
                  <span style={{ marginLeft: 6 }} title={selectedUser.country}>
                    {countryCodeToFlag(selectedUser.country)}
                  </span>
                )}
              </Typography>
            </Link>
            <Typography className={classes.membership}>
              {selectedUser.email}
            </Typography>
          </Box>
          {/* selectedUser.ref (internal state, set directly by handleSelect)
              reflects who's actually displayed right now — the value prop
              may not have round-tripped back through a parent's onChange
              yet, same reasoning as handleClear above. */}
          {renderMemberAction?.(selectedUser.ref)}
          <IconButton size="small" onClick={handleClear}>
            <ClearIcon fontSize="small" />
          </IconButton>
          </Box>
        </>
      ) : (
        <TextField
          fullWidth
          size="small"
          label={label}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onFocus={() => {
            if (users.length > 0 && query.trim().length >= 2) setOpen(true);
          }}
          variant="outlined"
          InputProps={{
            endAdornment: loading ? <CircularProgress size={14} /> : undefined,
          }}
        />
      )}

      {open && users.length > 0 && (
        <Paper className={classes.dropdown} elevation={4}>
          <List dense>
            {users.map(user => (
              <ListItem
                key={user.ref}
                className={classes.listItem}
                onClick={() =>
                  !disabledRefs.includes(user.ref) && handleSelect(user)
                }
                style={{
                  opacity: disabledRefs.includes(user.ref) ? 0.4 : 1,
                  cursor: disabledRefs.includes(user.ref)
                    ? 'not-allowed'
                    : 'pointer',
                }}
              >
                <ListItemAvatar>
                  <Avatar src={user.picture} className={classes.avatarResult}>
                    {user.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={formatPickerLabel(user.name, user.role, user.office)}
                  secondary={
                    <>
                      <span>{user.email}</span>
                      {user.memberships.length > 0 && (
                        <span className={classes.membership}>
                          {' · '}
                          {user.memberships.join(', ')}
                        </span>
                      )}
                    </>
                  }
                />
                {disabledRefs.includes(user.ref) && (
                  <ListItemSecondaryAction>
                    <Typography variant="caption">Already assigned</Typography>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}
